import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Link,
} from '@react-pdf/renderer';
import { WalletCards } from './components/WalletCards';
import { MonthlyCalendar } from './components/MonthlyCalendar';
import { QuickReference } from './components/QuickReference';

/**
 * Emergency Plan PDF Template
 *
 * Professional 15-25 page emergency preparedness plan
 * Built with React-PDF for high-quality rendering
 * 
 * Enhanced with:
 * - Wallet-sized emergency cards
 * - Monthly preparedness calendar
 * - Quick reference guide
 */

// Type definitions for plan data
export interface EmergencyPlanData {
  // Interview data
  interview: {
    name: string;
    email?: string;
    phone?: string;
    location: {
      address: string;
      city?: string;
      state?: string;
      zip_code?: string;
      emergency_contact?: {
        name: string;
        phone: string;
      };
    };
    household?: {
      size: number;
      ages?: number[];
      pets?: any;
      special_needs?: string[];
      dietary_restrictions?: string[];
      meeting_locations?: {
        primary: string;
        secondary: string;
      };
      family_contacts?: {
        name: string;
        phone: string;
        role?: string;
      }[];
      cert_trained?: boolean;
      training_types?: string[];
      documents_secured?: string[];
    };
    property?: {
      type: string;
      ownership?: string;
      features?: string[];
      utility_shutoffs?: {
        gas?: string;
        water?: string;
        electric?: string;
      };
    };
    emergency_types: string[];
    medical_needs?: {
      conditions?: string[];
      medications?: string[];
      equipment?: string[];
      medication_supply_duration?: string;
      blood_type?: string;
      allergies?: string[];
    };
    budget_range?: string;
  };

  // Research data (from agents)
  location_risks?: any;
  emergency_services?: any;
  resources?: any;
  hazard_analysis?: any;

  // Compiled plan data (from Phase 3)
  compiled_plan?: {
    executive_summary?: {
      threat_level?: string;
      priority_actions?: string[];
      key_risks?: string[];
      readiness_score?: number;
    };
    monthly_calendar?: {
      month: string;
      focus_area: string;
      tasks: string[];
    }[];
    wallet_cards?: any[];
    quick_reference?: any[];
    emergency_procedures?: {
      type: string;
      immediate_actions: string[];
    }[];
  };

  // Metadata
  generated_at: string;
  version: string;
}

// PDF Styles - Professional typography and spacing
const styles = StyleSheet.create({
  // Page layout
  page: {
    padding: 50,
    fontFamily: 'Helvetica',
    fontSize: 11,
    lineHeight: 1.6,
    backgroundColor: '#ffffff',
  },

  // Cover page
  coverPage: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
  },
  coverTitle: {
    fontSize: 48,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    marginBottom: 20,
    textAlign: 'center',
  },
  coverSubtitle: {
    fontSize: 24,
    color: '#f97316',
    marginBottom: 40,
    textAlign: 'center',
  },
  coverInfo: {
    fontSize: 14,
    color: '#d1d5db',
    textAlign: 'center',
    marginBottom: 8,
  },
  coverFooter: {
    position: 'absolute',
    bottom: 50,
    fontSize: 10,
    color: '#9ca3af',
  },

  // Headers
  h1: {
    fontSize: 28,
    fontFamily: 'Helvetica-Bold',
    color: '#1a1a1a',
    marginBottom: 20,
    marginTop: 10,
  },
  h2: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: '#2d3748',
    marginBottom: 12,
    marginTop: 24,
    borderBottom: '2pt solid #f97316',
    paddingBottom: 6,
  },
  h3: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: '#4a5568',
    marginBottom: 8,
    marginTop: 16,
  },

  // Text styles
  paragraph: {
    marginBottom: 12,
    textAlign: 'justify',
  },
  bold: {
    fontFamily: 'Helvetica-Bold',
  },
  warning: {
    backgroundColor: '#fef3c7',
    padding: 12,
    marginVertical: 8,
    borderLeft: '4pt solid #f59e0b',
  },
  warningText: {
    color: '#92400e',
    fontSize: 10,
  },

  // Lists
  list: {
    marginLeft: 20,
    marginBottom: 12,
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  bullet: {
    width: 15,
  },
  listContent: {
    flex: 1,
  },

  // Tables
  table: {
    marginVertical: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #e5e7eb',
    paddingVertical: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    paddingVertical: 10,
    fontFamily: 'Helvetica-Bold',
    borderBottom: '2pt solid #d1d5db',
  },
  tableCell: {
    flex: 1,
    paddingHorizontal: 8,
  },

  // Sections
  section: {
    marginBottom: 20,
  },
  sectionHighlight: {
    backgroundColor: '#f0fdf4',
    padding: 16,
    marginVertical: 10,
    borderRadius: 4,
    border: '1pt solid #86efac',
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    textAlign: 'center',
    fontSize: 9,
    color: '#6b7280',
    borderTop: '1pt solid #e5e7eb',
    paddingTop: 8,
  },
  pageNumber: {
    fontSize: 9,
    color: '#9ca3af',
  },

  // Contact cards
  contactCard: {
    backgroundColor: '#fef2f2',
    padding: 12,
    marginVertical: 6,
    borderLeft: '4pt solid #ef4444',
  },
  contactName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
    marginBottom: 4,
  },
  contactDetail: {
    fontSize: 10,
    color: '#4a5568',
    marginBottom: 2,
  },

  // Checklist
  checklistItem: {
    flexDirection: 'row',
    marginVertical: 4,
    paddingLeft: 10,
  },
  checkbox: {
    width: 12,
    height: 12,
    border: '1pt solid #6b7280',
    marginRight: 8,
    marginTop: 2,
  },
  checklistText: {
    flex: 1,
    fontSize: 10,
  },
});

// Component: Cover Page
const CoverPage = ({ data }: { data: EmergencyPlanData }): React.ReactElement => (
  <Page size="A4" style={styles.coverPage}>
    <View style={{ padding: 50, alignItems: 'center' }}>
      <Text style={styles.coverTitle}>Emergency</Text>
      <Text style={styles.coverTitle}>Preparedness Plan</Text>
      <Text style={styles.coverSubtitle}>Family Emergency Response Guide</Text>

      <View style={{ marginTop: 60 }}>
        <Text style={styles.coverInfo}>Prepared for: {data.interview.name}</Text>
        <Text style={styles.coverInfo}>
          Location: {data.interview.location.city}, {data.interview.location.state}
        </Text>
        <Text style={styles.coverInfo}>
          Generated: {new Date(data.generated_at).toLocaleDateString()}
        </Text>
        <Text style={styles.coverInfo}>Version: {data.version}</Text>
      </View>

      <Text style={styles.coverFooter}>
        This document contains critical emergency information. Keep copies in multiple locations.
      </Text>
    </View>
  </Page>
);

// Component: Table of Contents
const TableOfContents: React.FC = () => (
  <Page size="A4" style={styles.page}>
    <Text style={styles.h1}>Table of Contents</Text>

    <View style={styles.section}>
      <Text style={{ ...styles.bold, marginBottom: 8 }}>1. Executive Summary .................. 3</Text>
      <Text style={{ marginBottom: 8, marginLeft: 20 }}>Quick Reference Guide</Text>

      <Text style={{ ...styles.bold, marginBottom: 8, marginTop: 12 }}>
        2. Risk Assessment .................. 5
      </Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Location-Specific Risks</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Property Vulnerabilities</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Household Considerations</Text>

      <Text style={{ ...styles.bold, marginBottom: 8, marginTop: 12 }}>
        3. Emergency Services .................. 8
      </Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Hospitals and Medical Facilities</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Fire and Police Stations</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Emergency Shelters</Text>

      <Text style={{ ...styles.bold, marginBottom: 8, marginTop: 12 }}>
        4. Evacuation Plans .................. 11
      </Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Evacuation Routes</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Meeting Points</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Transportation Plans</Text>

      <Text style={{ ...styles.bold, marginBottom: 8, marginTop: 12 }}>
        5. Emergency Supplies .................. 14
      </Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>72-Hour Emergency Kit</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Medical Supplies</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Special Needs Items</Text>

      <Text style={{ ...styles.bold, marginBottom: 8, marginTop: 12 }}>
        6. Emergency Contacts .................. 17
      </Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Family Communication Plan</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Out-of-Area Contact</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Critical Phone Numbers</Text>

      <Text style={{ ...styles.bold, marginBottom: 8, marginTop: 12 }}>
        7. Emergency Procedures .................. 19
      </Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Earthquake Response</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Wildfire Response</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Flood Response</Text>

      <Text style={{ ...styles.bold, marginBottom: 8, marginTop: 12 }}>
        8. Resources & Maps .................. 23
      </Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Supply Stores</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Utility Shutoff Locations</Text>
      <Text style={{ marginBottom: 4, marginLeft: 20 }}>Important Documents</Text>
    </View>

    <Text style={styles.footer}>Emergency Preparedness Plan - {new Date().getFullYear()}</Text>
  </Page>
);

// Component: Executive Summary
const ExecutiveSummary: React.FC<{ data: EmergencyPlanData }> = ({ data }) => (
  <Page size="A4" style={styles.page}>
    <Text style={styles.h1}>Executive Summary</Text>

    <View style={styles.warning}>
      <Text style={styles.warningText}>
        ⚠ CRITICAL: Review this plan with all household members. Update contact information
        quarterly.
      </Text>
    </View>

    <Text style={styles.h2}>Quick Reference</Text>

    <View style={styles.section}>
      <Text style={{ ...styles.bold, marginBottom: 8 }}>
        Primary Emergencies for Your Area:
      </Text>
      <View style={styles.list}>
        {data.interview.emergency_types.map((type, idx) => (
          <View key={idx} style={styles.listItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.listContent}>{type.toUpperCase()}</Text>
          </View>
        ))}
      </View>
    </View>

    <View style={styles.section}>
      <Text style={{ ...styles.bold, marginBottom: 8 }}>Household Information:</Text>
      <Text style={styles.paragraph}>
        • Total members: {data.interview.household?.size || 1}
      </Text>
      <Text style={styles.paragraph}>• Property type: {data.interview.property?.type || 'Not specified'}</Text>
      {data.interview.household?.pets && (
        <Text style={styles.paragraph}>• Pets: Yes (see section 5 for supplies)</Text>
      )}
      {data.interview.medical_needs && (
        <Text style={styles.paragraph}>• Medical needs: Yes (see section 5 for details)</Text>
      )}
      {data.interview.household?.dietary_restrictions && data.interview.household.dietary_restrictions.length > 0 && (
        <Text style={styles.paragraph}>• Dietary restrictions: Yes (see section 5)</Text>
      )}
    </View>

    <View style={styles.sectionHighlight}>
      <Text style={{ ...styles.bold, marginBottom: 8 }}>
        IMMEDIATE ACTION CHECKLIST:
      </Text>
      <View style={styles.checklistItem}>
        <View style={styles.checkbox} />
        <Text style={styles.checklistText}>
          All family members have read this plan
        </Text>
      </View>
      <View style={styles.checklistItem}>
        <View style={styles.checkbox} />
        <Text style={styles.checklistText}>
          Emergency contact cards printed and distributed
        </Text>
      </View>
      <View style={styles.checklistItem}>
        <View style={styles.checkbox} />
        <Text style={styles.checklistText}>
          72-hour emergency kit assembled and accessible
        </Text>
      </View>
      <View style={styles.checklistItem}>
        <View style={styles.checkbox} />
        <Text style={styles.checklistText}>Meeting points identified and communicated</Text>
      </View>
      <View style={styles.checklistItem}>
        <View style={styles.checkbox} />
        <Text style={styles.checklistText}>
          Evacuation routes practiced (at least once)
        </Text>
      </View>
      <View style={styles.checklistItem}>
        <View style={styles.checkbox} />
        <Text style={styles.checklistText}>
          Important documents copied and stored securely
        </Text>
      </View>
    </View>

    <Text style={styles.footer}>
      Page 3 | Executive Summary
    </Text>
  </Page>
);

// Component: Risk Assessment Section
const RiskAssessment: React.FC<{ data: EmergencyPlanData }> = ({ data }) => (
  <Page size="A4" style={styles.page}>
    <Text style={styles.h1}>Risk Assessment</Text>

    <Text style={styles.paragraph}>
      Based on your location in {data.interview.location.city}, {data.interview.location.state},
      and property characteristics, this section outlines your primary emergency risks.
    </Text>

    <Text style={styles.h2}>Location-Specific Risks</Text>

    {data.location_risks ? (
      <View style={styles.section}>
        {data.interview.emergency_types.includes('earthquake') && (
          <View style={styles.section}>
            <Text style={styles.h3}>Earthquake Risk</Text>
            <Text style={styles.paragraph}>
              Your area is subject to seismic activity. The Cascadia Subduction Zone poses a
              significant threat with potential for 9.0+ magnitude earthquakes.
            </Text>
            <View style={styles.warning}>
              <Text style={styles.warningText}>
                ⚠ Prepare for potential ground shaking, aftershocks, and infrastructure damage.
              </Text>
            </View>
          </View>
        )}

        {data.interview.emergency_types.includes('wildfire') && (
          <View style={styles.section}>
            <Text style={styles.h3}>Wildfire Risk</Text>
            <Text style={styles.paragraph}>
              Your location is in or near a wildfire-prone area. Dry conditions and vegetation
              increase risk during summer months.
            </Text>
            <View style={styles.warning}>
              <Text style={styles.warningText}>
                ⚠ Monitor air quality alerts and be ready for rapid evacuation if ordered.
              </Text>
            </View>
          </View>
        )}

        {data.interview.emergency_types.includes('flood') && (
          <View style={styles.section}>
            <Text style={styles.h3}>Flood Risk</Text>
            <Text style={styles.paragraph}>
              Your area may experience flooding from heavy rainfall or nearby water sources.
            </Text>
            <View style={styles.warning}>
              <Text style={styles.warningText}>
                ⚠ Know your elevation and plan multiple evacuation routes to higher ground.
              </Text>
            </View>
          </View>
        )}
      </View>
    ) : (
      <Text style={styles.paragraph}>
        Detailed risk analysis data is being compiled. Please consult local emergency management
        for specific risk assessments.
      </Text>
    )}

    <Text style={styles.h2}>Property Vulnerabilities</Text>

    <Text style={styles.paragraph}>Property type: {data.interview.property?.type || 'Not specified'}</Text>

    <View style={styles.list}>
      <View style={styles.listItem}>
        <Text style={styles.bullet}>•</Text>
        <Text style={styles.listContent}>
          Identify structural weak points (windows, foundation)
        </Text>
      </View>
      <View style={styles.listItem}>
        <Text style={styles.bullet}>•</Text>
        <Text style={styles.listContent}>Secure heavy furniture and appliances</Text>
      </View>
      <View style={styles.listItem}>
        <Text style={styles.bullet}>•</Text>
        <Text style={styles.listContent}>Know utility shutoff locations (gas, water, electric)</Text>
      </View>
      <View style={styles.listItem}>
        <Text style={styles.bullet}>•</Text>
        <Text style={styles.listContent}>
          Clear defensible space around property (wildfire areas)
        </Text>
      </View>
    </View>

    <Text style={styles.footer}>
      Page 5 | Risk Assessment
    </Text>
  </Page>
);

// Component: Emergency Services Section
const EmergencyServices: React.FC<{ data: EmergencyPlanData }> = ({ data }) => (
  <Page size="A4" style={styles.page}>
    <Text style={styles.h1}>Emergency Services</Text>

    <Text style={styles.paragraph}>
      The following emergency services are located near your address. Keep this information
      accessible in both digital and printed formats.
    </Text>

    <Text style={styles.h2}>Hospitals & Medical Facilities</Text>

    {data.emergency_services?.hospitals?.length > 0 ? (
      data.emergency_services.hospitals.slice(0, 3).map((hospital: any, idx: number) => (
        <View key={idx} style={styles.contactCard}>
          <Text style={styles.contactName}>{hospital.name}</Text>
          <Text style={styles.contactDetail}>📍 {hospital.address}</Text>
          <Text style={styles.contactDetail}>📏 Distance: {hospital.distance} miles</Text>
          {hospital.phone && (
            <Text style={styles.contactDetail}>📞 {hospital.phone}</Text>
          )}
          {hospital.emergency_department && (
            <Text style={styles.contactDetail}>✓ 24/7 Emergency Department</Text>
          )}
          {hospital.trauma_level && (
            <Text style={styles.contactDetail}>⚕ {hospital.trauma_level} Trauma Center</Text>
          )}
        </View>
      ))
    ) : (
      <Text style={styles.paragraph}>
        Call 911 for emergency medical services. Locate nearest hospital using GPS.
      </Text>
    )}

    <Text style={styles.h2}>Fire Stations</Text>

    {data.emergency_services?.fire_stations?.length > 0 ? (
      data.emergency_services.fire_stations.slice(0, 2).map((station: any, idx: number) => (
        <View key={idx} style={styles.contactCard}>
          <Text style={styles.contactName}>{station.name}</Text>
          <Text style={styles.contactDetail}>📍 {station.address}</Text>
          <Text style={styles.contactDetail}>📏 Distance: {station.distance} miles</Text>
          {station.phone && (
            <Text style={styles.contactDetail}>📞 {station.phone}</Text>
          )}
        </View>
      ))
    ) : (
      <Text style={styles.paragraph}>Call 911 for fire emergencies.</Text>
    )}

    <Text style={styles.h2}>Emergency Shelters</Text>

    {data.emergency_services?.shelters?.length > 0 ? (
      data.emergency_services.shelters.slice(0, 3).map((shelter: any, idx: number) => (
        <View key={idx} style={styles.contactCard}>
          <Text style={styles.contactName}>{shelter.name}</Text>
          <Text style={styles.contactDetail}>📍 {shelter.address}</Text>
          <Text style={styles.contactDetail}>📏 Distance: {shelter.distance} miles</Text>
          <Text style={styles.contactDetail}>👥 Capacity: ~{shelter.capacity} people</Text>
          {shelter.services?.includes('pet-friendly') && (
            <Text style={styles.contactDetail}>🐾 Pet-Friendly</Text>
          )}
        </View>
      ))
    ) : (
      <Text style={styles.paragraph}>
        Contact Red Cross at 1-800-RED-CROSS for shelter information during emergencies.
      </Text>
    )}

    <Text style={styles.footer}>
      Page 8 | Emergency Services
    </Text>
  </Page>
);

// Component: Supply Checklist
const SupplyChecklist: React.FC<{ data: EmergencyPlanData }> = ({ data }) => (
  <Page size="A4" style={styles.page}>
    <Text style={styles.h1}>Emergency Supply Checklist</Text>

    <Text style={styles.paragraph}>
      Prepare a 72-hour emergency kit with these essential supplies. Store in an easily accessible
      location known to all household members.
    </Text>

    <Text style={styles.h2}>Water & Food</Text>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>
        Water: 1 gallon per person per day (3 gallons × {data.interview.household?.size || 1} people =
        {' '}{3 * (data.interview.household?.size || 1)} gallons minimum)
      </Text>
    </View>
    {data.interview.household?.dietary_restrictions && data.interview.household.dietary_restrictions.length > 0 && (
      <View style={styles.checklistItem}>
        <View style={styles.checkbox} />
        <Text style={styles.checklistText}>
          Special dietary needs: {data.interview.household.dietary_restrictions.join(', ')}
        </Text>
      </View>
    )}
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>
        Non-perishable food (3-day supply per person)
      </Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Manual can opener</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Paper plates, cups, utensils</Text>
    </View>

    <Text style={styles.h2}>First Aid & Medical</Text>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>First aid kit (bandages, antiseptic, pain relievers)</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>
        Prescription medications (7-day supply minimum{data.interview.medical_needs?.medication_supply_duration ? ` - Current supply: ${data.interview.medical_needs.medication_supply_duration}` : ''})
      </Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Medical supplies (glasses, hearing aids, etc.)</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Thermometer and basic medical tools</Text>
    </View>

    <Text style={styles.h2}>Tools & Safety</Text>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Flashlights and extra batteries</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Battery-powered or hand-crank radio (NOAA weather radio)</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Multi-tool or Swiss Army knife</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Duct tape and plastic sheeting</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Fire extinguisher (ABC type)</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Whistle for signaling</Text>
    </View>

    <Text style={styles.h2}>Personal Items</Text>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>
        Change of clothes and sturdy shoes for each person
      </Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Sleeping bags or blankets</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>
        Personal hygiene items (soap, toothbrush, sanitary supplies)
      </Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Cash and important documents (waterproof container)</Text>
    </View>
    <View style={styles.checklistItem}>
      <View style={styles.checkbox} />
      <Text style={styles.checklistText}>Phone chargers and power banks</Text>
    </View>

    {data.interview.household?.pets && (
      <>
        <Text style={styles.h2}>Pet Supplies</Text>
        <View style={styles.checklistItem}>
          <View style={styles.checkbox} />
          <Text style={styles.checklistText}>
            Pet food and water (3-day supply)
          </Text>
        </View>
        <View style={styles.checklistItem}>
          <View style={styles.checkbox} />
          <Text style={styles.checklistText}>
            Medications and medical records
          </Text>
        </View>
        <View style={styles.checklistItem}>
          <View style={styles.checkbox} />
          <Text style={styles.checklistText}>
            Leash, carrier, or crate
          </Text>
        </View>
        <View style={styles.checklistItem}>
          <View style={styles.checkbox} />
          <Text style={styles.checklistText}>
            Pet identification and vaccination records
          </Text>
        </View>
      </>
    )}

    <Text style={styles.footer}>
      Page 14 | Emergency Supplies
    </Text>
  </Page>
);

// Component: Emergency Contacts
const EmergencyContacts: React.FC<{ data: EmergencyPlanData }> = ({ data }) => (
  <Page size="A4" style={styles.page}>
    <Text style={styles.h1}>Emergency Contacts</Text>

    <Text style={styles.paragraph}>
      Complete this section and ensure all household members have a copy. Designate an
      out-of-state contact person for family coordination.
    </Text>

    <Text style={styles.h2}>Critical Numbers</Text>

    <View style={styles.table}>
      <View style={styles.tableHeader}>
        <Text style={styles.tableCell}>Service</Text>
        <Text style={styles.tableCell}>Phone Number</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.tableCell}>Emergency (Police, Fire, Medical)</Text>
        <Text style={styles.tableCell}>911</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.tableCell}>Poison Control</Text>
        <Text style={styles.tableCell}>1-800-222-1222</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.tableCell}>Red Cross</Text>
        <Text style={styles.tableCell}>1-800-RED-CROSS</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.tableCell}>Local Non-Emergency Police</Text>
        <Text style={styles.tableCell}>_______________</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.tableCell}>Electric Utility</Text>
        <Text style={styles.tableCell}>_______________</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.tableCell}>Gas Utility</Text>
        <Text style={styles.tableCell}>_______________</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.tableCell}>Water Utility</Text>
        <Text style={styles.tableCell}>_______________</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.tableCell}>Insurance Agent</Text>
        <Text style={styles.tableCell}>_______________</Text>
      </View>
    </View>

    <Text style={styles.h2}>Family Communication Plan</Text>

    <Text style={styles.paragraph}>
      Primary Contact Phone: {data.interview.phone || '_______________'}
    </Text>

    <Text style={styles.paragraph}>
      Out-of-State Emergency Contact (for family members to check in):
    </Text>

    {data.interview.location?.emergency_contact ? (
      <View style={styles.contactCard}>
        <Text style={styles.contactName}>{data.interview.location.emergency_contact.name}</Text>
        <Text style={styles.contactDetail}>📞 {data.interview.location.emergency_contact.phone}</Text>
      </View>
    ) : (
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Name:</Text>
          <Text style={styles.tableCell}>_____________________________</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Phone:</Text>
          <Text style={styles.tableCell}>_____________________________</Text>
        </View>
      </View>
    )}

    {data.interview.household?.meeting_locations && (
      <>
        <Text style={styles.h2}>Family Meeting Locations</Text>
        <Text style={styles.paragraph}>
          <Text style={styles.bold}>Primary Meeting Point:</Text>{'\n'}
          {data.interview.household.meeting_locations.primary}
        </Text>
        <Text style={styles.paragraph}>
          <Text style={styles.bold}>Secondary Meeting Point (if primary unavailable):</Text>{'\n'}
          {data.interview.household.meeting_locations.secondary}
        </Text>
      </>
    )}

    {data.interview.household?.documents_secured && data.interview.household.documents_secured.length > 0 && (
      <>
        <Text style={styles.h2}>Important Documents Status</Text>
        <Text style={{ fontSize: 10, marginBottom: 8 }}>
          Documents you've already secured in waterproof/fireproof storage:
        </Text>
        {data.interview.household.documents_secured.map((doc, idx) => (
          <View key={idx} style={styles.checklistItem}>
            <Text style={{ ...styles.checkbox, backgroundColor: '#22c55e', borderColor: '#22c55e' }} />
            <Text style={styles.checklistText}>{doc}</Text>
          </View>
        ))}
        <Text style={{ fontSize: 10, marginTop: 12, color: '#6b7280' }}>
          Additional documents to secure: Birth certificates, Social Security cards, Insurance policies, Property deeds, Medical records, Passports, Bank account information, Pet vaccination records
        </Text>
      </>
    )}

    {data.interview.household?.cert_trained && (
      <>
        <Text style={styles.h2}>Emergency Preparedness Training</Text>
        <Text style={styles.paragraph}>
          ✓ Household member(s) have completed emergency preparedness training
        </Text>
        {data.interview.household.training_types && data.interview.household.training_types.length > 0 && (
          <View style={styles.list}>
            {data.interview.household.training_types.map((training, idx) => (
              <View key={idx} style={styles.listItem}>
                <Text style={styles.bullet}>•</Text>
                <Text style={styles.listContent}>{training}</Text>
              </View>
            ))}
          </View>
        )}
        <Text style={{ fontSize: 10, marginTop: 8, color: '#059669' }}>
          Continue to practice emergency drills and review procedures with all household members quarterly.
        </Text>
      </>
    )}

    <Text style={styles.h2}>Household Member Contact Info</Text>

    <Text style={{ fontSize: 10, color: '#6b7280', marginBottom: 12 }}>
      List all household members with current contact information:
    </Text>

    {Array.from({ length: data.interview.household?.size || 1 }).map((_, idx) => (
      <View key={idx} style={{ marginBottom: 16, paddingBottom: 8, borderBottom: '1pt solid #e5e7eb' }}>
        <Text style={{ fontSize: 10, marginBottom: 4 }}>
          Member {idx + 1}: _________________________________
        </Text>
        <Text style={{ fontSize: 10, marginBottom: 4 }}>
          Phone: ___________________ Email: ___________________
        </Text>
      </View>
    ))}

    <Text style={styles.footer}>
      Page 17 | Emergency Contacts
    </Text>
  </Page>
);

// Helper to build wallet card data
const buildWalletCardData = (data: EmergencyPlanData) => ({
  name: data.interview.name,
  phone: data.interview.phone,
  email: data.interview.email,
  address: data.interview.location?.address,
  blood_type: data.interview.medical_needs?.blood_type,
  allergies: data.interview.medical_needs?.allergies,
  medications: data.interview.medical_needs?.medications,
  conditions: data.interview.medical_needs?.conditions,
  emergency_contact: data.interview.location?.emergency_contact,
  family_contacts: data.interview.household?.family_contacts,
  meeting_points: data.interview.household?.meeting_locations ? {
    primary: data.interview.household.meeting_locations.primary,
    secondary: data.interview.household.meeting_locations.secondary,
  } : undefined,
});

// Helper to build quick reference data
const buildQuickReferenceData = (data: EmergencyPlanData) => ({
  name: data.interview.name,
  address: data.interview.location?.address,
  emergency_types: data.interview.emergency_types,
  meeting_points: data.interview.household?.meeting_locations ? {
    primary: data.interview.household.meeting_locations.primary,
    secondary: data.interview.household.meeting_locations.secondary,
  } : undefined,
  utility_shutoffs: data.interview.property?.utility_shutoffs,
  local_emergency: data.emergency_services?.local_non_emergency,
});

// Main Document Component
export const EmergencyPlanDocument = ({ data }: { data: EmergencyPlanData }): React.ReactElement => (
  <Document>
    <CoverPage data={data} />
    <TableOfContents />
    <ExecutiveSummary data={data} />
    <RiskAssessment data={data} />
    <EmergencyServices data={data} />
    <SupplyChecklist data={data} />
    <EmergencyContacts data={data} />
    
    {/* New Enhanced Sections */}
    <QuickReference 
      data={buildQuickReferenceData(data)}
      procedures={data.compiled_plan?.emergency_procedures}
    />
    <MonthlyCalendar 
      calendar={data.compiled_plan?.monthly_calendar}
    />
    <WalletCards 
      data={buildWalletCardData(data)}
    />
  </Document>
);
